// src/components/operationCenter/widgets/ETFFlowsWidget.tsx
// Bitcoin ETF inflows/outflows chart widget

import React, { useMemo, useRef, useEffect, useState } from "react";
import * as echarts from "echarts";
import type { EChartsOption, ECharts } from "echarts";
import { DollarSign } from "lucide-react";
import type { WidgetProps } from "@/types/dashboard";
import { useETFInflowChart } from "@/hooks/coinglass/useETFInflowChart";

const ETFFlowsWidget: React.FC<WidgetProps> = ({ widget }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<ECharts | null>(null);
  const [containerSize, setContainerSize] = useState({
    width: 400,
    height: 200,
  });

  // Get ticker from widget config, default to btc
  const config = widget.config as Record<string, unknown>;
  const ticker = (config?.["ticker"] as string) ?? "btc";
  const { data, isLoading, error } = useETFInflowChart(ticker);

  // Measure container and resize chart
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
        // Resize chart if it exists
        if (chartInstanceRef.current) {
          chartInstanceRef.current.resize();
        }
      }
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Process chart data
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return { dates: [], values: [] };

    // Get last 90 days
    const recentData = data.slice(-90);

    return {
      dates: recentData.map((d) => d.date),
      // Net flow = inflow - outflow
      values: recentData.map((d) => (d.inflow ?? 0) - (d.outflow ?? 0)),
    };
  }, [data]);

  // Render chart
  useEffect(() => {
    if (!chartRef.current || chartData.dates.length === 0) return;

    const chart = chartInstanceRef.current || echarts.init(chartRef.current);
    chartInstanceRef.current = chart;

    const option: EChartsOption = {
      backgroundColor: "transparent",
      grid: { top: 20, right: 12, bottom: 24, left: 12, containLabel: true },
      tooltip: {
        trigger: "axis",
        backgroundColor: "rgba(17, 24, 39, 0.95)",
        borderColor: "#374151",
        textStyle: { color: "#f3f4f6", fontSize: 11 },
        formatter: (params: unknown) => {
          if (!Array.isArray(params) || params.length === 0) return "";
          const p = params[0] as { axisValue: string; value: number };
          const val = p.value;
          const formatted =
            Math.abs(val) >= 1e9
              ? `$${(val / 1e9).toFixed(2)}B`
              : Math.abs(val) >= 1e6
              ? `$${(val / 1e6).toFixed(2)}M`
              : `$${val.toLocaleString()}`;
          return `<div style="font-weight:500;margin-bottom:4px;color:#9ca3af;">${
            p.axisValue
          }</div>
            <div style="font-weight:600;color:${
              val >= 0 ? "#22c55e" : "#ef4444"
            };">
              Net Flow: ${formatted}
            </div>`;
        },
      },
      xAxis: {
        type: "category",
        data: chartData.dates,
        axisLine: { lineStyle: { color: "#374151" } },
        axisLabel: {
          color: "#6b7280",
          fontSize: 9,
          interval: Math.floor(chartData.dates.length / 6),
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: "value",
        axisLine: { show: false },
        axisLabel: {
          color: "#6b7280",
          fontSize: 9,
          formatter: (v: number) =>
            Math.abs(v) >= 1e9
              ? `${(v / 1e9).toFixed(1)}B`
              : Math.abs(v) >= 1e6
              ? `${(v / 1e6).toFixed(0)}M`
              : v.toString(),
        },
        splitLine: { lineStyle: { color: "#374151", type: "dashed" } },
      },
      series: [
        {
          type: "bar",
          data: chartData.values.map((v) => ({
            value: v,
            itemStyle: { color: v >= 0 ? "#22c55e" : "#ef4444" },
          })),
          barWidth: "60%",
        },
      ],
    };

    chart.setOption(option, true);

    return () => {
      // Cleanup handled in separate effect
    };
  }, [chartData, containerSize]);

  // Cleanup
  useEffect(() => {
    return () => {
      chartInstanceRef.current?.dispose();
      chartInstanceRef.current = null;
    };
  }, []);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-slate-500 text-sm">Loading...</div>
      </div>
    );
  }

  if (error || chartData.dates.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-500 p-4">
        <DollarSign className="w-8 h-8 mb-2 opacity-50" />
        <div className="text-sm">No ETF flow data available</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700/50 flex-shrink-0 bg-slate-800/30">
        <DollarSign className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-xs uppercase tracking-wide text-slate-300">
          {ticker.toUpperCase()} ETF Net Flows
        </span>
      </div>

      {/* Chart */}
      <div ref={containerRef} className="flex-1 min-h-0">
        <div ref={chartRef} style={{ width: "100%", height: "100%" }} />
      </div>
    </div>
  );
};

export default ETFFlowsWidget;
